import { z } from 'zod';
import { zPrintErrorType } from './enums';

/** Extend with 'NO_ERROR' */
const zPrinterError = z.union([zPrintErrorType, z.literal('NO_ERROR')]).default('NO_ERROR');

export const VIRTUAL_TERMINAL_IDS = ['T1', 'T2', 'T3'] as const;
export type VirtualTerminalId = (typeof VIRTUAL_TERMINAL_IDS)[number];

/**
 * Web (dev/e2e) only: stable per-terminal iPad tokens so the dev "Terminal" switcher acts as
 * distinct iPads. Web is simulation-only and never mints a random secret — it always uses one of
 * these. The e2e seed hashes these same strings into `iPads.tokenHash`, so a seeded iPad resolves
 * for each terminal (keep the two in sync via this single source of truth).
 */
export const WEB_VIRTUAL_TERMINAL_TOKENS = {
  T1: 'dev-virtual-terminal-t1',
  T2: 'dev-virtual-terminal-t2',
  T3: 'dev-virtual-terminal-t3',
} satisfies Record<VirtualTerminalId, string>;

/** posDevTools is not defined, unless toggled on by the developer. */
export const posDevToolsSchema = z.object({
  /** Allows online status to be set to `false` for testing purposes. */
  isOfflineOverride: z.boolean().default(false),

  /** Indicates if the POS DevTools panel visible or not. */
  isVisible: z.boolean().default(true), // TODO: Set to false in production and trigger on with magic tap

  /** Virtual printers */
  lanPrinter3Connected: z.boolean().default(false),
  lanPrinter3Error: zPrinterError,
  lanPrinter3PrintDelay: z.number().default(2000),
  lanPrinter4Connected: z.boolean().default(false),
  lanPrinter4Error: zPrinterError,
  lanPrinter4PrintDelay: z.number().default(2000),
  usbPrinter1Connected: z.boolean().default(false),
  usbPrinter1Error: zPrinterError,
  usbPrinter1PrintDelay: z.number().default(2000),
  usbPrinter2Connected: z.boolean().default(false),
  usbPrinter2Error: zPrinterError,
  usbPrinter2PrintDelay: z.number().default(2000),
  /** Discovers `VIRTUAL_UNSUPPORTED_USB_PRINTER`, to exercise the "not supported" pairing path. */
  unsupportedUsbPrinterConnected: z.boolean().default(false),

  /** Used for 'web' to simulate different iOS terminals. */
  virtualTerminalId: z.literal(VIRTUAL_TERMINAL_IDS).default('T1'),
});
export type PosDevTools = z.output<typeof posDevToolsSchema>;

/**
 * Per-terminal dev-tools flags, so the web "Terminal" switcher gives each virtual iPad (T1/T2/T3)
 * its own persisted printer setup / offline state. Keyed by `virtualTerminalId`, which is 1:1 with
 * the iPad token on web. Slots are optional — created lazily when a terminal is first used.
 */
export const posDevToolsByTerminalSchema = z.object({
  T1: posDevToolsSchema.optional(),
  T2: posDevToolsSchema.optional(),
  T3: posDevToolsSchema.optional(),
});
export type PosDevToolsByTerminal = z.output<typeof posDevToolsByTerminalSchema>;
